/**
 * The social compositions, in one place so they can be mounted into more than one root.
 *
 * They ship inside the tripmovie bundle — that is the bundle the render worker serves — and are
 * also registered standalone in ./index.tsx for local renders. Defining them once means the two
 * can't drift.
 */
import { Composition } from "remotion";
import { BookendReel } from "./BookendReel";
import { NightsCard, RankedStory, TrendingStory } from "./StillCards";
import { DuoPin, ListPin, PinCard, StatPin } from "./PinCards";
import { RANKED_REEL_FRAMES, RankedReel } from "./RankedReel";
import { ClipReel, ClipReelProps } from "./ClipReel";
import { clipReelDuration } from "./clipReelTiming";

export const SocialCompositions: React.FC = () => (
	<>
		<Composition
			id="bookend-reel"
			component={BookendReel}
			width={1080}
			height={1920}
			fps={30}
			durationInFrames={240}
			defaultProps={{
				city: "Bangkok",
				pct: 38,
				contrastCity: "Koh Tao",
				contrastPct: 2.8,
				contrastNights: 4,
				cityPhoto: "https://images.unsplash.com/photo-1508009603885-50cf7c579365?fm=jpg&q=80&w=1080&h=1920&fit=crop",
				midPhoto: "https://images.unsplash.com/photo-1686238349945-82074ce9f02f?fm=jpg&q=80&w=1080&h=1920&fit=crop",
			}}
		/>
		<Composition
			id="ranked-reel"
			component={RankedReel}
			width={1080}
			height={1920}
			fps={30}
			durationInFrames={RANKED_REEL_FRAMES}
			defaultProps={{
				eyebrow: "Real trip data",
				hook: "How many nights do you actually need in Thailand?",
				hookPhoto: "https://images.pexels.com/photos/30970845/pexels-photo-30970845.jpeg?auto=compress&cs=tinysrgb&w=1080&h=1920&fit=crop",
				outro: "Now build the rest of it.",
				outroSub: "Free trip planner",
				items: [
					{
						name: "Koh Tao",
						value: "4",
						note: "You go once, and you stay",
						photo: "https://images.pexels.com/photos/1450372/pexels-photo-1450372.jpeg?auto=compress&cs=tinysrgb&w=1080&h=1920&fit=crop",
					},
					{
						name: "Krabi",
						value: "3",
						note: "Same as Chiang Mai and Phuket",
						photo: "https://images.pexels.com/photos/2161449/pexels-photo-2161449.jpeg?auto=compress&cs=tinysrgb&w=1080&h=1920&fit=crop",
					},
					{
						name: "Koh Samui",
						value: "3",
						note: "One stay, start to finish",
						photo: "https://images.pexels.com/photos/1450353/pexels-photo-1450353.jpeg?auto=compress&cs=tinysrgb&w=1080&h=1920&fit=crop",
					},
					{
						name: "Bangkok",
						value: "2",
						note: "38% stop here twice",
						photo: "https://images.pexels.com/photos/1682748/pexels-photo-1682748.jpeg?auto=compress&cs=tinysrgb&w=1080&h=1920&fit=crop",
					},
				],
			}}
		/>
		<Composition
			id="clip-reel"
			component={ClipReel}
			width={1080}
			height={1920}
			fps={30}
			durationInFrames={600}
			// The only composition here whose length is not knowable up front: a cut is however many
			// shots it takes. Every shot is capped at three seconds inside the component, so this can
			// only ever total shots x 90 frames at the very most.
			calculateMetadata={({ props }: { props: ClipReelProps }) => ({ durationInFrames: clipReelDuration(props.shots) })}
			defaultProps={{
				eyebrow: "Thailand",
				hook: "The country in twenty seconds.",
				outro: "Now build your own.",
				outroSub: "Free trip planner",
				// Real clips from the curated gallery (explore/media/thailand in stippl-knowledge).
				//
				// THE CAPTIONS ARE SCENE LEVEL, NOT DESTINATION LEVEL, AND THAT IS DELIBERATE. The
				// gallery files each clip under a destination, but `place_confidence` is "generic" and
				// `place_checked` is null on every video in it — no person ever confirmed the location,
				// unlike the photo layer. Checking thirty top-ranked Thailand clips by eye found a Laos
				// viewpoint and a European street filed as Pai and Koh Lanta, an aeroplane filed as
				// Khao Sok, and one street-food clip filed under two destinations at once.
				//
				// A small contact sheet does not catch it either: one clip survived that pass and was
				// only caught at full size, by a school uniform and the contributor's name — Philippine
				// street food, filed under Ayutthaya. So a caller passing `caption: "Krabi"` is making
				// a claim the gallery cannot back. Caption what is visibly in the frame until the video
				// layer carries a checked place.
				shots: [
					{ src: "https://videos.pexels.com/video-files/32107886/13688637_1080_1920_60fps.mp4", seconds: 2.5, startAt: 2, credit: "Pexels \u00b7 Peggy Anke" },
					{
						src: "https://videos.pexels.com/video-files/5859286/5859286-hd_1080_1920_30fps.mp4",
						seconds: 2.2,
						startAt: 2,
						caption: "Temples",
						credit: "Pexels \u00b7 Peggy Anke",
					},
					{
						src: "https://videos.pexels.com/video-files/35737444/15148101_2160_3840_60fps.mp4",
						seconds: 2.5,
						startAt: 2,
						caption: "Under the water",
						credit: "Pexels \u00b7 JUN HO LEE",
					},
					{ src: "https://videos.pexels.com/video-files/37595620/15933689_1080_1920_30fps.mp4", seconds: 2.5, startAt: 2, credit: "Pexels \u00b7 MARUF IQBAL" },
					{ src: "https://videos.pexels.com/video-files/26550058/11958245_1080_1920_30fps.mp4", seconds: 3, startAt: 2, credit: "Pexels \u00b7 Enam Sape" },
				],
			}}
		/>
		<Composition
			id="nights-card"
			component={NightsCard}
			width={1080}
			height={1350}
			fps={30}
			durationInFrames={1}
			defaultProps={{
				country: "Thailand",
				sampleLabel: "835,000 planned stops",
				photo: "https://images.unsplash.com/photo-1686238349945-82074ce9f02f?fm=jpg&q=80&w=1080&h=1920&fit=crop",
				rows: [
					{ name: "Koh Tao", nights: 4 },
					{ name: "Chiang Mai", nights: 3 },
					{ name: "Krabi", nights: 3 },
					{ name: "Phuket", nights: 3 },
					{ name: "Koh Samui", nights: 3 },
					{ name: "Bangkok", nights: 2, note: "38% stop twice" },
				],
			}}
		/>
		<Composition
			id="pin-card"
			component={PinCard}
			width={1000}
			height={1500}
			fps={30}
			durationInFrames={1}
			defaultProps={{
				title: "How many nights do you need in Thailand?",
				eyebrow: "Real trip data",
				unit: "nights",
				photo: "https://images.unsplash.com/photo-1686238349945-82074ce9f02f?fm=jpg&q=80&w=1000&h=600&fit=crop",
				rows: [
					{ name: "Koh Tao", value: 4 },
					{ name: "Chiang Mai", value: 3 },
					{ name: "Krabi", value: 3 },
					{ name: "Koh Samui", value: 3 },
					{ name: "Bangkok", value: 2, note: "38% stop twice" },
				],
			}}
		/>
		<Composition
			id="pin-stat"
			component={StatPin}
			width={1000}
			height={1500}
			fps={30}
			durationInFrames={1}
			defaultProps={{
				eyebrow: "Bangkok",
				value: "2",
				lede: "nights. That is all most people give it.",
				footnote: "Median of 203,656 planned stops",
				photo: "https://images.pexels.com/photos/1682748/pexels-photo-1682748.jpeg?auto=compress&cs=tinysrgb&w=1000&h=1500&fit=crop",
			}}
		/>
		<Composition
			id="pin-list"
			component={ListPin}
			width={1000}
			height={1500}
			fps={30}
			durationInFrames={1}
			defaultProps={{
				eyebrow: "Rising now \u00b7 last 30 days",
				title: "Where Japan is heading this autumn",
				// ListPin's panel is 520 tall, not the 600 pin-card uses. The previous default was also
				// the cities.ts "Hakone" photo, which is Chureito Pagoda at Fujiyoshida: a different
				// destination, and one this pipeline clusters separately.
				photo: "https://images.unsplash.com/photo-1509023464722-18d996393ca8?fm=jpg&q=80&w=1000&h=520&fit=crop",
				rows: [
					{ name: "Hakone", value: "+54%" },
					{ name: "Kanazawa", value: "+49%" },
					{ name: "Nara", value: "+17%" },
					{ name: "Kyoto", value: "+14%" },
				],
			}}
		/>
		<Composition
			id="pin-duo"
			component={DuoPin}
			width={1000}
			height={1500}
			fps={30}
			durationInFrames={1}
			defaultProps={{
				band: "Nights per stop \u00b7 stippl.io",
				top: {
					value: "4",
					name: "Koh Tao",
					sub: "You go once, and you stay",
					photo: "https://images.pexels.com/photos/1450372/pexels-photo-1450372.jpeg?auto=compress&cs=tinysrgb&w=1000&h=700&fit=crop",
				},
				bottom: {
					value: "2",
					name: "Bangkok",
					sub: "38% pass through twice",
					photo: "https://images.pexels.com/photos/1682748/pexels-photo-1682748.jpeg?auto=compress&cs=tinysrgb&w=1000&h=700&fit=crop",
				},
			}}
		/>
		<Composition
			id="trending-story"
			component={TrendingStory}
			width={1080}
			height={1920}
			fps={30}
			durationInFrames={1}
			defaultProps={{
				headline: "Japan's autumn is being planned right now.",
				windowLabel: "last 30 days",
				// Ask the CDN for the exact canvas size. Without h and fit=crop you get the source
				// aspect and objectFit crops it invisibly, which is what these defaults teach.
				photo: "https://images.unsplash.com/photo-1749352133990-67bf2a652149?fm=jpg&q=80&w=1080&h=1920&fit=crop",
				rows: [
					{ name: "Kanazawa", growthPct: 52 },
					{ name: "Hakone", growthPct: 50 },
					{ name: "Nara", growthPct: 17 },
					{ name: "Kyoto", growthPct: 12 },
				],
			}}
		/>
		<Composition
			id="ranked-story"
			component={RankedStory}
			width={1080}
			height={1920}
			fps={30}
			durationInFrames={1}
			defaultProps={{
				eyebrow: "Most planned · Tokyo",
				headline: "What people actually put in a Tokyo plan.",
				photo: "https://images.pexels.com/photos/2506923/pexels-photo-2506923.jpeg?auto=compress&cs=tinysrgb&w=1080&h=1920&fit=crop",
				rows: [
					{ name: "Sensō-ji", note: "Level with Shibuya Crossing" },
					{ name: "Shibuya Crossing" },
					{ name: "teamLab Planets" },
					{ name: "Shibuya Sky" },
					{ name: "Meiji Jingu" },
				],
				footnote: "Ranked by how often each appears in a Tokyo plan",
			}}
		/>
	</>
);

export default SocialCompositions;
